import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Button, buttonClassName } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type { AcceptInvitationResult } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";

import { AuthScreenLayout } from "./AuthScreenLayout";

/**
 * `/accept-invitation?token=...` (gap-fill spec, screen 2) — `POST
 * /auth/accept-invitation`. The exact path and query-param shape the invite
 * email's own link already hardcodes
 * (`app/domain/identity/invitations.py:130`,
 * `f"{app_base_url}/accept-invitation?token={token}"`) — this route must
 * match it exactly, not choose a different shape, so `token` is read from
 * `useSearchParams()` and sent unmodified in the request body (VC-GF1-09).
 *
 * `POST /auth/accept-invitation` returns only `{accepted: true}` — no
 * session (`AcceptInvitationResult`'s own shape, confirmed by reading
 * `app/api/auth.py`). This screen never attempts to auto-sign-in: success
 * is a plain confirmation plus a real link to `/sign-in` (judgement call 3,
 * VC-GF1-06). Pre-filling the email on that link is not possible — the
 * response carries no email address — so none is attempted.
 *
 * A refused token (unknown, expired, already used, or the invited person
 * was removed) renders `invitations.ACCEPTANCE_REFUSAL_MESSAGE` verbatim,
 * the same one sentence for every cause (AC, VC-GF1-04) — no branch here
 * tries to tell them apart.
 */
export function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [accepted, setAccepted] = useState(false);

  const mismatch = confirmPassword !== "" && password !== confirmPassword;
  const canSubmit = token !== "" && password.length >= 8 && password === confirmPassword;

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      await apiFetch<AcceptInvitationResult>("/auth/accept-invitation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      setAccepted(true);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  if (accepted) {
    return (
      <AuthScreenLayout title="Password set">
        <p className="text-body text-ink-secondary">
          Your password is set. Sign in with your email address and new password to get started.
        </p>
        <Link to="/sign-in" className={buttonClassName("primary")}>
          Go to sign in
        </Link>
      </AuthScreenLayout>
    );
  }

  if (token === "") {
    return (
      <AuthScreenLayout title="Invitation link incomplete">
        <p role="alert" className="text-body text-danger">
          This invitation link is missing its token. Please use the link from your invitation email, or ask the
          Owner for a new one.
        </p>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout title="Set your password">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("password")}</span>
          <Input
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">Confirm password</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </label>

        {mismatch ? (
          <p role="alert" className="text-meta text-danger">
            Those passwords don&apos;t match.
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? "Setting password…" : "Set password"}
        </Button>
      </form>
    </AuthScreenLayout>
  );
}
